import React from "react";
import type { CharacterData } from "~/types/storage";
import { computeHpMax, computeResilienceMax } from "~/utils/characterDerived";
import styles from "~/pages/GmView/widgets/MinimizedCharacterCard/MinimizedCharacterCard.scss";

export interface MinimizedCharacterCardProps {
  character: CharacterData;
  onRestore: () => void;
  onDelete: () => void;
  onOpen?: () => void;
}

/**
 * MinimizedCharacterCard Component
 *
 * A very compact card for minimized characters in GM view.
 * Shows character name, core stats, HP/Res inline, with restore and delete buttons.
 */
const MinimizedCharacterCard: React.FC<MinimizedCharacterCardProps> = ({
  character,
  onRestore,
  onDelete,
  onOpen,
}) => {
  const mind = character.coreStats["Mind"] ?? 0;
  const body = character.coreStats["Body"] ?? 0;
  const reflex = character.coreStats["Reflex"] ?? 0;
  const hpMax = computeHpMax(character.coreStats);
  const resMax = computeResilienceMax(character.coreStats);

  const handleNameClick = () => {
    onOpen?.();
  };

  return (
    <div className={styles.card}>
      <span
        className={`${styles.name} ${onOpen ? styles.clickable : ""}`}
        title={onOpen ? `Open ${character.name} in new window` : character.name}
        onClick={onOpen ? handleNameClick : undefined}
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleNameClick(); } } : undefined}
      >
        {character.name}
      </span>

      <span className={styles.stats}>
        <span className={styles.stat}>M{mind}</span>
        <span className={styles.stat}>B{body}</span>
        <span className={styles.stat}>R{reflex}</span>
      </span>

      <span className={styles.resources}>
        <span className={styles.resource}>
          HP {character.currentHp}/{hpMax}
        </span>
        <span className={styles.resource}>
          Res {character.currentResilience}/{resMax}
        </span>
        {character.strain > 0 && (
          <span className={styles.strain}>
            Str {character.strain}
          </span>
        )}
      </span>

      <div className={styles.actions}>
        {onOpen && (
          <button
            type="button"
            className={styles.openButton}
            onClick={onOpen}
            title="Open in new window"
          >
            ↗
          </button>
        )}
        <button
          type="button"
          className={styles.restoreButton}
          onClick={onRestore}
          title="Restore character"
        >
          +
        </button>
        <button
          type="button"
          className={styles.deleteButton}
          onClick={onDelete}
          title="Delete character"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default MinimizedCharacterCard;
